import { useHandleObjList } from "hooks/useHandleObjList"
import tinycolor from "tinycolor2"

import { useCallback, useMemo, useState } from "react"
import {
  initialBannerInfoForm,
  initialExternalProductInfo,
  initialMarketplaceInfo,
  initialProductFilesObj,
  initialProductInfo,
  LIMIT_BENEFITS_CHARACTERS
} from "constants/product"
import { ColorService, useColor } from "react-color-palette"
import { defaultProductBannerColor, defaultProductBannerColorSecondary } from "../constants/defaultValues"

const useProduct = () => {
  const [emailError, setEmailError] = useState("")
  const [descriptionError, setDescriptionError] = useState("")
  const [productName, setProductName] = useState("")
  const [isFederated, setIsFederated] = useState<boolean>(false)
  const [isExternal, setIsExternal] = useState<boolean>(false)
  const [federatedKey, setFederatedKey] = useState<string>("")
  const [productInfo, setProductInfo] = useState<App.ProductInfo>(initialProductInfo)
  const [federationFields, setFederationFields] = useState<App.Integrations[]>([])
  const [externalFields, setExternalFields] = useState<App.ExternalProductInfo>(initialExternalProductInfo)
  const [files, setFiles] = useState<App.ProductFilesObj>(initialProductFilesObj)
  const [productPrefixUrl, setProductPrefixUrl] = useState("")
  const [marketplaceInfo, setMarketplaceInfo] = useState<App.MarketplaceInfo>(initialMarketplaceInfo)
  const [bannerInfoForm, setBannerInfoForm] = useState<App.BannerInfoForm>(initialBannerInfoForm)
  const [color, setColor] = useColor(defaultProductBannerColor)
  const [colorSecondary, setColorSecondary] = useColor(defaultProductBannerColorSecondary)

  const {
    list: benefitsList,
    addElementAtEnd: addBenefit,
    removeElement: removeBenefit,
    changeElement: changeBenefit,
    replaceList: replaceBenefitsList
  } = useHandleObjList<App.IBenefitItem>({ value: "" })

  const isValidBenefits = useMemo(() => {
    return (
      benefitsList.length > 0 &&
      benefitsList.every((benefit) => benefit.value.length > 0 && benefit.value.length <= LIMIT_BENEFITS_CHARACTERS)
    )
  }, [benefitsList])

  const isValidProductForm = useMemo(() => {
    return (
      productName.length > 0 &&
      productPrefixUrl.length > 0 &&
      (files.logoUrl || productInfo.logoUrl.length > 0) &&
      (files.image || productInfo.image.length > 0) &&
      productInfo.description.length > 0 &&
      !descriptionError &&
      productInfo.supportEmail.length > 0 &&
      !emailError &&
      federatedKey.length > 0
    )
  }, [
    productName,
    productPrefixUrl,
    files.logoUrl,
    files.image,
    productInfo.logoUrl,
    productInfo.image,
    productInfo.description,
    descriptionError,
    productInfo.supportEmail,
    emailError,
    federatedKey
  ])

  const isValidFederatedForm = useMemo(() => {
    return !isFederated || productInfo.federationUrl.length > 0
  }, [isFederated, productInfo.federationUrl])

  const isValidExternalForm = useMemo(() => {
    return (
      !isExternal ||
      (externalFields.externalApiUrl.length > 0 &&
        externalFields.authMethod.length > 0 &&
        externalFields.authKey.length > 0 &&
        externalFields.docTitle.length > 0 &&
        externalFields.docDescription.length > 0)
    )
  }, [isExternal, externalFields])

  const isValidBannerInfoForm = useMemo(() => {
    return (bannerInfoForm.imageFile || marketplaceInfo.bannerInfo.bannerImageUrl.length > 0) && color.hex.length > 0
  }, [bannerInfoForm, marketplaceInfo, color])

  const isValidIntegrationForm = useMemo(() => {
    return (
      federationFields.length === 0 ||
      federationFields.every(
        (integration) =>
          integration.key.length > 0 &&
          integration.label.length > 0 &&
          integration.type.length > 0 &&
          (integration.type !== "file" || integration.externalEndpointUrlForFileManagement.length > 0)
      )
    )
  }, [federationFields])

  const isValidProduct = useMemo(() => {
    return isValidProductForm && isValidFederatedForm && isValidExternalForm && isValidIntegrationForm
  }, [isValidProductForm, isValidFederatedForm, isValidExternalForm, isValidIntegrationForm])

  const defineInitialColors = useCallback((bannerInfo: App.BannerInfo) => {
    let { color, colorSecondary } = bannerInfo

    const newColor = tinycolor(color)
    const newColorSecondary = tinycolor(colorSecondary)

    if (!newColor.isValid()) {
      color = initialBannerInfoForm.hex
    }

    if (!newColorSecondary.isValid()) {
      colorSecondary = initialBannerInfoForm.hexSecondary
    }

    setColor(ColorService.convert("hex", color))
    setColorSecondary(ColorService.convert("hex", colorSecondary))

    return { color, colorSecondary }
  }, [])

  const overWriteProductData = useCallback((product: App.Product) => {
    const { color, colorSecondary } = defineInitialColors(product.marketplaceInfo.bannerInfo)

    setProductName(product.name || "")
    setIsFederated(product.isFederated || false)
    setIsExternal(product.isExternal || false)
    setFederatedKey(product.federatedKey || "")
    setProductInfo({
      description: product.productInfo.description || "",
      supportEmail: product.productInfo.supportEmail || "",
      federationUrl: product.productInfo.federationUrl || "",
      logoUrl: product.productInfo.logoUrl || "",
      image: product.productInfo.image || "",
      productLandingPageURL: product.productInfo.productLandingPageURL || "",
      isPublished: product.productInfo.isPublished || true
    })
    setFederationFields(product.federationFields || [])
    setExternalFields({
      authMethod: product.productInfo.authMethod || "",
      authKey: product.productInfo.authKey || "",
      docLink: product.productInfo.docLink || "",
      externalApiAuthUrl: product.productInfo.externalApiAuthUrl || "",
      externalApiUrl: product.productInfo.externalApiUrl || "",
      docTitle: product.productInfo.docTitle || "",
      docDescription: product.productInfo.docDescription || ""
    })
    setProductPrefixUrl(product.prefixUrl || "")
    setBannerInfoForm({
      imageFile: undefined,
      hex: color,
      hexSecondary: colorSecondary,
      isGradient: product.marketplaceInfo.bannerInfo.isGradient || initialBannerInfoForm.isGradient
    })
    setMarketplaceInfo({
      benefits: product.marketplaceInfo.benefits || [],
      bannerInfo: {
        bannerImageUrl: product.marketplaceInfo.bannerInfo.bannerImageUrl || "",
        color: product.marketplaceInfo.bannerInfo.color || "",
        colorSecondary: product.marketplaceInfo.bannerInfo.colorSecondary || "",
        isGradient: product.marketplaceInfo.bannerInfo.isGradient || initialBannerInfoForm.isGradient
      }
    })

    replaceBenefitsList(product.marketplaceInfo.benefits.map((benefit: string) => ({ value: benefit })))
  }, [])

  const handleBannerInfoFormChange = useCallback(
    <K extends keyof App.BannerInfoForm>(key: K, value: App.BannerInfoForm[K]) => {
      setBannerInfoForm((prevState) => ({
        ...prevState,
        [key]: value
      }))
    },
    []
  )

  const handleBannerInfoFormConfirm = useCallback(() => {
    setMarketplaceInfo((prevState) => ({
      ...prevState,
      bannerInfo: {
        ...prevState.bannerInfo,
        color: color.hex,
        colorSecondary: colorSecondary.hex,
        isGradient: bannerInfoForm.isGradient
      }
    }))

    setFiles((prevState) => ({
      ...prevState,
      bannerImageUrl: bannerInfoForm.imageFile
    }))
  }, [color.hex, colorSecondary.hex, bannerInfoForm])

  const handleBannerInfoFormCancel = useCallback(() => {
    const { color, colorSecondary } = defineInitialColors(marketplaceInfo.bannerInfo)

    setBannerInfoForm({
      imageFile: files.bannerImageUrl || undefined,
      hex: color,
      hexSecondary: colorSecondary,
      isGradient: marketplaceInfo.bannerInfo.isGradient
    })
  }, [files, marketplaceInfo.bannerInfo])

  return {
    isValidProduct,
    overWriteProductData,
    productToSubmit: {
      data: {
        name: productName,
        prefixUrl: productPrefixUrl,
        isFederated,
        isExternal,
        federatedKey,
        federationFields,
        productInfo: {
          ...productInfo,
          ...externalFields
        },
        marketplaceInfo,
        files
      }
    },
    formData: {
      productForm: {
        productName,
        setProductName,
        productPrefixUrl,
        setProductPrefixUrl,
        setFiles,
        logoFile: files.logoUrl,
        image: files.image,
        emailError,
        setEmailError,
        descriptionError,
        setDescriptionError,
        federatedKey,
        setFederatedKey
      },
      bannerInfo: {
        logoFile: files.logoUrl,
        logoUrl: productInfo.logoUrl,
        color,
        setColor,
        colorSecondary,
        setColorSecondary,
        bannerImageUrl: marketplaceInfo.bannerInfo.bannerImageUrl,
        bannerInfoForm,
        isValidBannerInfoForm,
        handleBannerInfoFormChange,
        handleBannerInfoFormConfirm,
        handleBannerInfoFormCancel
      },
      isFederatedForm: {
        isFederated,
        setIsFederated
      },
      isExternalForm: {
        externalFields,
        setExternalFields,
        isExternal,
        setIsExternal
      },
      integrationForm: {
        integrationList: federationFields,
        setIntegrationList: setFederationFields
      },
      productInfo: {
        productInfo,
        setProductInfo
      }
    },
    benefits: {
      benefitsList,
      addBenefit,
      changeBenefit,
      removeBenefit,
      disabled: !isValidBenefits || !isValidProduct
    }
  }
}

export default useProduct
